import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../contexts/AuthContext';
import { getNotes, addNote, deleteNote, updateNote } from '../services/firestoreService';
import './NotesPage.css';
import Header from '../components/Header';

// --- Componente TagsList ---
const TagsList = ({ tagStats, totalNotes, onTagClick }) => (
  <div className="tags-list-container">
    <div className="tags-list">
      <div 
        className="tag-item all-notes"
        onClick={() => onTagClick('TODAS')}
      >
        <span className="tag-name">TODAS</span>
        <span className="tag-count">({totalNotes})</span>
      </div>
      {tagStats.map(tag => (
        <div 
          key={tag.name}
          className="tag-item"
          onClick={() => onTagClick(tag.name)}
        >
          <span className="tag-name">{tag.name}</span>
          <span className="tag-count">({tag.count})</span>
        </div>
      ))}
    </div>
  </div>
);

// --- Componente FilteredNotesList ---
const FilteredNotesList = ({ 
  notes, 
  selectedTag, 
  onBackClick, 
  selectedNoteId, 
  onNoteSelect 
}) => (
  <div className="filtered-notes-container">
    <div className="notes-list-header">
      <button className="back-button" onClick={onBackClick}>
        ← Tags
      </button>
      <h3>{selectedTag === 'TODAS' ? 'Todas las Notas' : `Tag: ${selectedTag}`} ({notes.length})</h3>
    </div>
    <div className="notes-list">
      {notes.length === 0 ? (
        <div className="empty-state">
          No hay notas para este tag
        </div>
      ) : (
        notes.map((note) => (
          <NoteListItem
            key={note.id}
            note={note}
            isSelected={note.id === selectedNoteId}
            onSelect={onNoteSelect}
          />
        ))
      )}
    </div>
  </div>
);

// --- Componente NoteForm con sistema de tags mejorado ---
const NoteForm = ({ formId, initialData = { title: '', content: '', tags: [] }, onSubmit, onCancel }) => {
  const [title, setTitle] = useState(initialData.title);
  const [content, setContent] = useState(initialData.content);
  const [tags, setTags] = useState(initialData.tags || []);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    setTitle(initialData.title);
    setContent(initialData.content);
    setTags(initialData.tags || []);
    setTagInput('');
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const isEmpty = !content || content.replace(/<(.|\n)*?>/g, '').trim().length === 0;
    if (!title.trim() || isEmpty) {
      alert('Título y contenido no pueden estar vacíos.');
      return;
    }
    onSubmit({ title, content, tags });
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedTag = tagInput.trim();
      if (trimmedTag && !tags.includes(trimmedTag)) {
        setTags([...tags, trimmedTag]);
        setTagInput('');
      }
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Quill modules with proper configuration
  const modules = {
    toolbar: [
      [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
      [{size: []}],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, 
       {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video'
  ];

  return (
    <div className="note-editor-container">
      <form onSubmit={handleSubmit} className="note-form" id={formId}>
        <div className="form-row">
          <div className="form-group title-group">
            <input
              type="text"
              id="note-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título de la nota..."
              className="title-input"
              required
            />
          </div>
          <div className="form-group tags-group">
            <input
              type="text"
              id="note-tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleTagInputKeyPress}
              placeholder="Agregar tag (Enter para añadir)"
              className="tags-input"
            />
          </div>
        </div>
        
        {/* Display tags as chips below title */}
        {tags.length > 0 && (
          <div className="tags-display">
            {tags.map((tag, index) => (
              <span key={index} className="tag-chip">
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="tag-remove"
                  title="Eliminar tag"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        
        <div className="form-group editor-group">
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            scrollingContainer=".note-editor-container .ql-container"
            placeholder="Escribe tu nota aquí..."
            style={{ height: '100%' }}
          />
        </div>
      </form>
    </div>
  );
};

// --- Componente NoteListItem ---
const NoteListItem = ({ note, isSelected, onSelect }) => (
  <div
    className={`note-list-item ${isSelected ? 'selected' : ''}`}
    onClick={() => onSelect(note.id)}
  >
    <h4 className="note-list-item-title">{note.title || '(Sin título)'}</h4>
    <div className="note-list-item-preview">
      {note.content ? note.content.replace(/<[^>]*>/g, '').substring(0, 100) + '...' : 'Sin contenido'}
    </div>
    {note.tags && note.tags.length > 0 && (
      <div className="note-list-item-tags">
        {note.tags.slice(0, 3).map((tag, index) => (
          <span key={index} className="tag-chip-small">{tag}</span>
        ))}
        {note.tags.length > 3 && <span className="tag-chip-small">+{note.tags.length - 3}</span>}
      </div>
    )}
    <div className="note-list-item-date">
      {note.updatedAt && typeof note.updatedAt.toDate === 'function'
        ? note.updatedAt.toDate().toLocaleDateString()
        : 'Reciente'}
    </div>
  </div>
);

// Objeto constante para la nota vacía
const emptyNoteData = { title: '', content: '', tags: [] };

// --- Página Principal de Notas ---
function NotesPage() {
  const { currentUser } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado para edición/selección
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [showNewNoteForm, setShowNewNoteForm] = useState(false);

  // Estado para búsqueda
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para navegación de tags
  const [sidebarView, setSidebarView] = useState('tags'); // 'tags' | 'notes'
  const [selectedTag, setSelectedTag] = useState(null); // tag seleccionado o 'TODAS'
  const [filteredNotes, setFilteredNotes] = useState([]); // notas filtradas por tag

  // Función para calcular estadísticas de tags
  const calculateTagStats = useCallback((notes) => {
    const tagCounts = {};
    notes.forEach(note => {
      note.tags?.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });
    
    return Object.entries(tagCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count); // Más frecuente primero
  }, []);

  // Función para filtrar notas por tag
  const filterNotesByTag = useCallback((notes, tag) => {
    if (tag === 'TODAS') return notes;
    return notes.filter(note => note.tags?.includes(tag));
  }, []);

  // Calcular tag stats usando useMemo para performance
  const tagStats = useMemo(() => calculateTagStats(notes), [notes, calculateTagStats]);

  // Handlers para navegación de tags
  const handleTagClick = useCallback((tagName) => {
    setSelectedTag(tagName);
    const filtered = filterNotesByTag(notes, tagName);
    setFilteredNotes(filtered);
    setSidebarView('notes'); // Cambiar a vista de notas
    // Limpiar selección de nota al cambiar de tag
    setSelectedNoteId(null);
    setEditingNote(null);
    setShowNewNoteForm(false);
  }, [notes, filterNotesByTag]);

  const handleBackToTags = useCallback(() => {
    setSidebarView('tags'); // Volver a vista de tags
    setSelectedTag(null);
    setFilteredNotes([]);
    // Limpiar selección de nota al volver a tags
    setSelectedNoteId(null);
    setEditingNote(null);
    setShowNewNoteForm(false);
  }, []);

  // Función para cargar notas
  const fetchNotes = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const userNotes = await getNotes(currentUser.uid);
      setNotes(userNotes);
    } catch (err) {
      console.error("Error fetching notes:", err);
      setError('Error al cargar las notas. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Cargar notas al montar el componente
  useEffect(() => {
    document.title = 'PEC - Notas';
    if (!currentUser) return;
    fetchNotes();
  }, [currentUser, fetchNotes]);

  // Actualizar el `editingNote` cuando `selectedNoteId` cambia
  useEffect(() => {
    if (selectedNoteId) {
      // Buscar en la lista correcta según la vista actual
      const notesList = sidebarView === 'notes' ? filteredNotes : notes;
      const noteToEdit = notesList.find(note => note.id === selectedNoteId);
      setEditingNote(noteToEdit);
      setShowNewNoteForm(false);
    } else {
      setEditingNote(null);
    }
  }, [selectedNoteId, notes, filteredNotes, sidebarView]);

  // Manejador para el submit del formulario
  const handleFormSubmit = async (noteData) => {
    if (!currentUser) return setError('Debes estar logueado.');
    if (!noteData.title?.trim() || !noteData.content?.trim()) {
      return setError('Título y contenido no pueden estar vacíos.');
    }

    setIsSubmitting(true);
    setError('');

    try {
      let newNoteId = null;
      if (editingNote) {
        await updateNote(currentUser.uid, editingNote.id, noteData);
      } else {
        const docRef = await addNote(currentUser.uid, noteData);
        newNoteId = docRef.id;
      }
      await fetchNotes();
      if (newNoteId) {
        setSelectedNoteId(newNoteId);
        setShowNewNoteForm(false);
      }
    } catch (err) {
      console.error("Error saving note:", err);
      setError(`Error al ${editingNote ? 'actualizar' : 'añadir'} la nota.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Seleccionar una nota de la lista
  const handleSelectNote = (noteId) => {
    setSelectedNoteId(noteId);
    setShowNewNoteForm(false);
  };

  // Iniciar una nueva nota
  const handleAddNewNote = () => {
    setSelectedNoteId(null);
    setEditingNote(null);
    setShowNewNoteForm(true);
  };

  // Eliminar una nota
  const handleDeleteNote = async () => {
    if (!selectedNoteId || !editingNote) return;
    if (!currentUser) return setError('Debes estar logueado para eliminar notas.');
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la nota '${editingNote.title}'?`)) return;

    const noteIdToDelete = selectedNoteId;
    try {
      setSelectedNoteId(null);
      setEditingNote(null);
      setShowNewNoteForm(false);
      await deleteNote(currentUser.uid, noteIdToDelete);
      setNotes(prevNotes => prevNotes.filter(note => note.id !== noteIdToDelete));
    } catch (err) {
      console.error("Error deleting note:", err);
      setError('Error al eliminar la nota.');
      setSelectedNoteId(noteIdToDelete);
    }
  };

  // Filtrar las notas según el término de búsqueda
  const getNotesToShow = () => {
    const baseNotes = sidebarView === 'notes' ? filteredNotes : notes;
    if (!searchTerm) return baseNotes;
    
    return baseNotes.filter(note =>
      note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const notesToShow = getNotesToShow();

  const addFormId = 'add-note-form';
  const editFormId = editingNote ? `edit-note-form-${editingNote.id}` : null;

  return (
    <div className="notes-page">
      <Header 
        currentPage="notes" 
        showSearch 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showAddButton 
        onAddNew={handleAddNewNote}
      />
      
      <div className="notes-layout">
        {/* Sidebar with tags/notes navigation */}
        <div className="notes-sidebar">
          <div className={`sidebar-content ${sidebarView === 'tags' ? 'show-tags' : 'show-notes'}`}>
            {/* Vista de Tags */}
            <div className="sidebar-view tags-view">
              {loading ? (
                <div className="loading-state">Cargando tags...</div>
              ) : (
                <TagsList 
                  tagStats={tagStats} 
                  totalNotes={notes.length}
                  onTagClick={handleTagClick} 
                />
              )}
            </div>
            
            {/* Vista de Notas Filtradas */}
            <div className="sidebar-view notes-view">
              <FilteredNotesList
                notes={notesToShow}
                selectedTag={selectedTag}
                onBackClick={handleBackToTags}
                selectedNoteId={selectedNoteId}
                onNoteSelect={handleSelectNote}
              />
            </div>
          </div>
        </div>

        {/* Main editor area */}
        <div className="notes-main">
          {/* Editing existing note */}
          {editingNote && !loading && (
            <>
              <NoteForm
                key={editFormId}
                formId={editFormId}
                initialData={editingNote}
                onSubmit={handleFormSubmit}
                onCancel={() => setSelectedNoteId(null)}
              />
              
              <div className="editor-actions">
                <button
                  type="submit"
                  form={editFormId}
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteNote}
                  className="btn btn-danger"
                  disabled={isSubmitting}
                >
                  Eliminar
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedNoteId(null)}
                  className="btn btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
              </div>
            </>
          )}

          {/* Creating new note */}
          {showNewNoteForm && !loading && (
            <>
              <NoteForm
                key={addFormId}
                formId={addFormId}
                initialData={emptyNoteData}
                onSubmit={handleFormSubmit}
                onCancel={() => setShowNewNoteForm(false)}
              />
              
              <div className="editor-actions">
                <button
                  type="submit"
                  form={addFormId}
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creando...' : 'Guardar Nota'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewNoteForm(false)}
                  className="btn btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
              </div>
            </>
          )}

          {/* Loading state */}
          {loading && <div className="loading-state">Cargando...</div>}
          
          {/* Welcome state - when no note is selected or being created */}
          {!loading && !editingNote && !showNewNoteForm && (
            <div className="welcome-state">
              <div className="welcome-icon">📝</div>
              <h3>Bienvenido a tus notas</h3>
              <p>
                {sidebarView === 'tags' 
                  ? 'Selecciona un tag de la lista para ver las notas o crea una nueva'
                  : 'Selecciona una nota de la lista o crea una nueva para comenzar'
                }
              </p>
            </div>
          )}

          {/* Error state */}
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    </div>
  );
}

export default NotesPage;
